import { useMemo } from 'react';

import { shouldUseProportionalSampling } from '@/components/nodeGraph/config/graphConfig';
import { useProportionalAssetsForGraph } from '@/components/nodeGraph/hooks/useProportionalAssets';
import { GraphEntityType } from '@/components/nodeGraph/utils/types';

import { useAssetData } from './useAssetData';
import { useConsolidatedGraph } from './useConsolidatedGraph';
import { useRelationships } from './useRelationships';

/**
 * Main composition hook for fetching and building the node graph data.
 * Orchestrates asset fetching, relationship queries, and graph construction.
 *
 * This hook follows the composition pattern:
 * 1. Fetch assets with pagination support (or proportional sampling if enabled)
 * 2. Extract asset keys for relationship queries
 * 3. Fetch all relationship data
 * 4. Build consolidated graph from all data
 *
 * @param clusterByEntity - Entity type to cluster the graph by (affects visualization and queries)
 * @param parentFilters - Optional filters to apply to asset queries
 * @param maxNodes - Maximum number of asset nodes to fetch (default: 1000)
 * @param clusterAttribute - Optional cluster attribute for proportional sampling
 * @param totalAssets - Optional total asset count for proportional sampling decision
 * @returns Object containing graph data, entities, loading states, and pagination controls
 */
export const useGraphData = (
  clusterByEntity?: GraphEntityType,
  parentFilters?: any,
  maxNodes: number = 1000,
  clusterAttribute?: string | null,
  totalAssets?: number
) => {
  // Determine if we should use proportional sampling
  const useProportional =
    clusterAttribute &&
    totalAssets &&
    shouldUseProportionalSampling(clusterAttribute, totalAssets, maxNodes);

  // Step 1a: Fetch assets with proportional sampling (if enabled)
  const proportionalData = useProportionalAssetsForGraph(
    parentFilters,
    maxNodes,
    useProportional || false
  );

  // Step 1b: Fetch assets with standard pagination (fallback or when proportional disabled)
  const standardData = useAssetData(
    parentFilters,
    maxNodes,
    !useProportional // Only enable when NOT using proportional
  );

  // Select the appropriate data source
  const assetData = useProportional ? proportionalData : standardData;

  // Step 2: Extract asset keys for relationship queries
  const assetKeys = useMemo(
    () => assetData.assets.map(asset => asset.key),
    [assetData.assets]
  );

  // Step 3: Fetch all relationship data
  const relationshipData = useRelationships(assetKeys, clusterByEntity);

  // Step 4: Build consolidated graph from all data
  const graphData = useConsolidatedGraph({
    assets: assetData.assets,
    relationships: relationshipData,
    clusterByEntity,
  });

  return {
    // Graph data
    consolidatedGraph: graphData.consolidatedGraph,

    // Entity arrays
    assets: assetData.assets,
    risks: relationshipData.risks,
    seeds: graphData.seeds,
    technologies: graphData.technologies,

    // Loading states
    isLoading: assetData.isLoading, // Only block on assets, not all data
    isLoadingRelatedData: relationshipData.isLoadingRelatedData,

    // Pagination controls
    hasNextPage: assetData.hasNextPage,
    fetchNextPage: assetData.fetchNextPage,

    // Filter management
    assetFilters: assetData.assetFilters,
    setAssetFilters: assetData.setAssetFilters,

    // Proportional sampling metadata
    distributions: useProportional
      ? (proportionalData as any).distributions
      : undefined,
  };
};
